import { Container, Sprite, Text, type Texture } from 'pixi.js';
import { chapterCard, type ChapterMoment } from '../../logic/road/landmarks';
import { BoardDialog, BrassButton, COLOURS, layoutButtonRow, uiStyle } from '../board/index';
import { balancedWrapWidth } from './balancedWrap';

/** The slot a finished chapter opened, and how to go and play it. */
export interface NextSlot {
  name: string;
  /** The slot has a road of its own to restore. */
  road: boolean;
  /** The player already has it (bought with gems): no card promises to
   *  open it. */
  owned?: boolean;
  play: () => void;
}

/**
 * The end of a road's chapter: the slot it opened (its map painting), and
 * the choice to go and play it now or stay a while. It says the slot is
 * open only when this chapter just opened it; with no next slot it just
 * says the road is done.
 */
export class ChapterDialog extends Container {
  private dialog: BoardDialog | null = null;
  private viewW = 1;
  private viewH = 1;

  constructor(
    private readonly texture: (alias: string) => Texture | null,
    private readonly floor: () => Texture | null,
  ) {
    super();
  }

  get isOpen(): boolean {
    return this.dialog !== null;
  }

  layout(w: number, h: number): void {
    this.viewW = w;
    this.viewH = h;
    this.dialog?.layout(w, h);
  }

  /** The card for road `themeId`'s chapter: why it's up is `moment`. */
  open(themeId: string, chapter: number, next: NextSlot | null, moment: ChapterMoment = 'again'): void {
    this.close();
    const card = chapterCard(chapter, next, moment);
    const dialog = new BoardDialog({ title: card.title, floor: this.floor() });
    dialog.layout(this.viewW, this.viewH);
    const inner = dialog.contentWidth;
    let y = 0;

    // Each road's next slot has its own alias: an alias keeps its first picture.
    const art = next ? this.texture(`road:next_slot_${themeId}`) : null;
    if (art) {
      const side = Math.max(110, Math.min(170, Math.round(this.viewH * 0.22)));
      const s = new Sprite(art);
      s.anchor.set(0.5, 0);
      s.scale.set(side / Math.max(art.width, art.height));
      s.position.set(inner / 2, y);
      dialog.content.addChild(s);
      y += side + 6;
    }
    const say = (
      text: string,
      size: number,
      weight: '500' | '700',
      fill: number = COLOURS.cream,
    ) => {
      const style = {
        ...uiStyle(size, fill, weight),
        align: 'center' as const,
        lineHeight: Math.round(size * 1.3),
      };
      const t = new Text({
        text,
        style: { ...style, wordWrap: true, wordWrapWidth: balancedWrapWidth(text, style, inner) },
      });
      t.anchor.set(0.5, 0);
      t.position.set(inner / 2, y);
      dialog.content.addChild(t);
      y += t.height + 8;
    };

    const close = () => this.close();
    let cancel: BrassButton | null = null;
    let action: BrassButton;
    if (next) {
      say(card.line, 16, '700', 0xffffff);
      cancel = new BrassButton({ label: 'Stay here', kind: 'wood', onTap: close });
      action = new BrassButton({
        label: 'PLAY NOW',
        kind: 'gold',
        onTap: () => {
          close();
          next.play();
        },
      });
    } else {
      say(card.line, 17, '700', 0xffffff);
      action = new BrassButton({ label: 'WONDERFUL', kind: 'gold', onTap: close });
    }
    y += 4;
    if (cancel) dialog.content.addChild(cancel);
    dialog.content.addChild(action);
    layoutButtonRow(cancel, action, 0, y, inner);
    y += action.buttonHeight + 3;
    dialog.setContentHeight(y);

    this.addChild(dialog);
    this.dialog = dialog;
    void dialog.open();
  }

  close(): void {
    const dialog = this.dialog;
    this.dialog = null;
    if (!dialog) return;
    void dialog.close().then(() => dialog.destroy({ children: true }));
  }
}
